import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, Info, Bell } from 'lucide-react';

// Toast Context
const TapInToastContext = createContext(null);

// Toast types with Tap In branding colors
const TOAST_TYPES = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-gradient-to-r from-green-500 to-emerald-600',
    iconColor: 'text-white',
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-gradient-to-r from-red-500 to-rose-600',
    iconColor: 'text-white',
  },
  info: {
    icon: Info,
    bgColor: 'bg-gradient-to-r from-blue-500 to-indigo-600',
    iconColor: 'text-white',
  },
  notification: {
    icon: Bell,
    bgColor: 'bg-gradient-to-r from-purple-500 to-violet-600',
    iconColor: 'text-white',
  },
};

// Individual Toast Component
const ToastItem = ({ toast, onDismiss }) => {
  const [isExiting, setIsExiting] = useState(false);
  const type = TOAST_TYPES[toast.type] || TOAST_TYPES.info;
  const Icon = type.icon;

  useEffect(() => {
    if (toast.duration !== Infinity) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toast.duration || 4000);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-2xl shadow-2xl backdrop-blur-xl
        ${type.bgColor}
        transform transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
        animate-slide-in-right
      `}
      style={{
        minWidth: '320px',
        maxWidth: '400px',
      }}
    >
      {/* Tap In Logo/Icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
        <Icon className={`w-5 h-5 ${type.iconColor}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Tap In Branding */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white/80 text-xs font-semibold tracking-wider">TAP IN</span>
        </div>
        
        {toast.title && (
          <p className="text-white font-bold text-sm">{toast.title}</p>
        )}
        {toast.message && (
          <p className="text-white/90 text-sm mt-0.5">{toast.message}</p>
        )}
      </div>

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
      >
        <X className="w-3.5 h-3.5 text-white" />
      </button>
    </div>
  );
};

// Toast Container
export const TapInToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((options) => {
    return addToast({
      type: options.type || 'info',
      title: options.title,
      message: options.message,
      duration: options.duration || 4000,
    });
  }, [addToast]);

  // Convenience methods
  const success = useCallback((title, message) => showToast({ type: 'success', title, message }), [showToast]);
  const error = useCallback((title, message) => showToast({ type: 'error', title, message }), [showToast]);
  const info = useCallback((title, message) => showToast({ type: 'info', title, message }), [showToast]);
  const notify = useCallback((title, message) => showToast({ type: 'notification', title, message }), [showToast]);

  return (
    <TapInToastContext.Provider value={{ showToast, success, error, info, notify, removeToast }}>
      {children}
      
      {/* Toast Container - Fixed Position */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onDismiss={removeToast} />
          </div>
        ))}
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </TapInToastContext.Provider>
  );
};

// Hook to use Tap In toasts
export const useTapInToast = () => {
  const context = useContext(TapInToastContext);
  if (!context) {
    throw new Error('useTapInToast must be used within a TapInToastProvider');
  }
  return context;
};

// Standalone function for showing toasts (for use outside React components)
let toastRef = null;

export const setToastRef = (ref) => {
  toastRef = ref;
};

export const tapInToast = {
  success: (title, message) => toastRef?.success(title, message),
  error: (title, message) => toastRef?.error(title, message),
  info: (title, message) => toastRef?.info(title, message),
  notify: (title, message) => toastRef?.notify(title, message),
};

export default TapInToastProvider;
